/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { query } from 'lit/decorators/query.js';
import { baseStyles, property, querySlot, querySlotAll, childrenUpdateComplete, getElementLanguageDirection, event, describeElementByElements, state, syncProps, pxToRem, getElementUpdates, hasAriaLabelTypeAttr, calculateOptimalLayout, responsive, elementVisible, } from '@cds/core/internal';
import styles from './control.element.scss';
import { syncHTML5Validation } from '../utils/validate.js';
import { associateInputToDatalist, getStatusIcon, associateInputAndLabel, controlIsWrapped, formLayouts, defaultFormLayout, defaultControlWidth, getCurrentMessageStatus, } from '../utils/utils.js';
export var ControlLabelLayout;
(function (ControlLabelLayout) {
    ControlLabelLayout["default"] = "default";
    ControlLabelLayout["ariaLabel"] = "aria-label";
    ControlLabelLayout["inputGroup"] = "input-group";
    ControlLabelLayout["hiddenLabel"] = "hidden-label";
})(ControlLabelLayout || (ControlLabelLayout = {}));
/**
 * Generic Control
 *
 * ```typescript
 * import '@cds/core/forms/register.js';
 * ```
 *
 * ```html
 * <cds-control>
 *   <label>control</label>
 *   <input type="text" />
 * </cds-control>
 * ```
 * @element cds-control
 * @slot - For projecting input and label
 * @cssprop --label-width
 */
let CdsControl = class CdsControl extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Set the status of form control validation
         * @type {neutral | error | success}
         */
        this.status = 'neutral';
        /**
         * Adjust the control from the default full width or the browser default width
         * @type {stretch | shrink}
         */
        this.controlWidth = defaultControlWidth;
        /**
         * Set the validate attribute to sync with HTML5 native validation
         * https://developer.mozilla.org/en-US/docs/Learn/Forms/Form_validation
         */
        this.validate = false;
        /**
         * By default forms will collapse to layout that prevents overflow.
         * If disabled control layout may break or overflow in unexpected ways.
         */
        this.responsive = true;
        this._layout = defaultFormLayout;
        this.focused = false;
        this.disabled = false;
        this.readonly = false;
        this.fixedControlWidth = false;
        this.supportsPrefixSuffixActions = true;
        /** @private */
        this.labelLayout = ControlLabelLayout.default;
        this.observers = [];
    }
    /**
     * Set to adjust the default control layout. When `responsive` is true this will be the largest size to scale to.
     * @type {vertical | horizontal | compact}
     */
    get layout() {
        return this._layout;
    }
    set layout(value) {
        const oldValue = this._layout;
        this._layout = value ? value.replace('-inline', '') : defaultFormLayout; // auto convert any form-layouts from parent groups
        this.requestUpdate('layout', oldValue);
    }
    get isRTL() {
        return getElementLanguageDirection(this) === 'rtl';
    }
    static get styles() {
        return [baseStyles, styles];
    }
    get hasAriaLabelTypeAttr() {
        return hasAriaLabelTypeAttr(this.inputControl);
    }
    get hasStatusIcon() {
        return this.labelLayout !== ControlLabelLayout.inputGroup && (this.status === 'error' || this.status === 'success');
    }
    render() {
        return html `
      ${this.labelLayout === ControlLabelLayout.hiddenLabel || this.labelLayout === ControlLabelLayout.inputGroup
            ? html `<span cds-layout="display:screen-reader-only"
            ><slot name="label" @slotchange=${() => this.associateInputAndLabel()}></slot
          ></span>`
            : ''}
      <div
        cds-layout="${this.layout === 'vertical' ? 'vertical gap:xs' : 'horizontal gap:lg'} align:stretch"
        class="private-host ${this.isRTL ? 'rtl' : ''}"
      >
        ${this.primaryLabelTemplate}
        <div
          class="input-message-container"
          cds-layout="
          wrap:none
          ${this.layout === 'compact' ? 'horizontal' : 'vertical'}
          ${this.controlWidth === 'stretch' && !this.fixedControlWidth ? 'align:horizontal-stretch' : ''}
          ${this.messages?.length ? 'gap:xs' : ''}"
        >
          <div cds-layout="horizontal gap:xs wrap:none">
            <div
              cds-layout="horizontal align:top wrap:none ${this.controlWidth === 'shrink' || this.fixedControlWidth
            ? 'align:shrink'
            : 'align:horizontal-stretch'}"
              class="${this.hasStatusIcon ? 'input-container with-status-icon' : 'input-container'}"
            >
              ${this.inputTemplate} ${this.prefixTemplate}
              <slot name="input"></slot>
              ${this.suffixTemplate}
            </div>
            ${this.hasStatusIcon ? getStatusIcon(this.status) : ''}
          </div>
          ${this.messagesTemplate}
          <slot name="datalist" @slotchange=${() => this.associateInputToDatalist()}></slot>
        </div>
      </div>
    `;
    }
    get inputTemplate() {
        return html ``;
    }
    get prefixDefaultTemplate() {
        return null;
    }
    get suffixDefaultTemplate() {
        return null;
    }
    get isGenericControl() {
        return this.tagName.toLowerCase() === 'cds-control';
    }
    get hasControlActions() {
        return this.controlActions.length > 0 || this.prefixDefaultTemplate || this.suffixDefaultTemplate;
    }
    get primaryLabelTemplate() {
        return html `
      ${this.labelLayout === ControlLabelLayout.default
            ? html ` <cds-internal-control-label
            .disabled="${this.disabled}"
            cds-layout="align:shrink align:top"
            action="primary"
          >
            <slot name="label" @slotchange=${() => this.associateInputAndLabel()}></slot>
          </cds-internal-control-label>`
            : ''}
    `;
    }
    get messagesTemplate() {
        return html `
      <div cds-layout="${this.layout === 'compact' ? 'align:shrink' : ''}" class="messages">
        <slot name="message"></slot>
      </div>
    `;
    }
    get prefixTemplate() {
        return html `
      <div cds-layout="align:shrink align:vertical-center" class="prefix">
        <div cds-layout="horizontal gap:xxs">
          ${this.prefixDefaultTemplate}
          <slot name="prefix"></slot>
        </div>
      </div>
    `;
    }
    get suffixTemplate() {
        return html `
      <div cds-layout="align:shrink align:vertical-center" class="suffix">
        <div cds-layout="horizontal gap:xxs">
          <slot name="suffix"></slot>
          ${this.suffixDefaultTemplate}
        </div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('cds-control', '');
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.setupHostAttributes();
        this.setupHTML5Validation();
        this.setupPositioningListeners();
        this.setupResponsive();
        this.setupDescribedByUpdates();
        this.setupLabelLayout();
        this.assignSlotIfInControlGroup();
    }
    updated(props) {
        super.updated(props);
        this.messages.forEach(message => syncProps(message, this, { disabled: props.has('disabled') }));
        syncProps(this.inputControl, this, { disabled: props.has('disabled') });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.observers.forEach(o => o?.disconnect());
    }
    associateInputAndLabel() {
        associateInputAndLabel(this.inputControl, this.label);
    }
    associateInputToDatalist() {
        associateInputToDatalist(this.inputControl, this.datalistControl);
    }
    setupDescribedByUpdates() {
        this.messageSlot?.addEventListener('slotchange', async () => {
            describeElementByElements(this.inputControl, Array.from(this.messages));
            getCurrentMessageStatus(Array.from(this.messages)).then(s => (this.status = s));
        });
    }
    setupHostAttributes() {
        this.inputControl.addEventListener('focusin', () => (this.focused = true));
        this.inputControl.addEventListener('focusout', () => (this.focused = false));
        this.observers.push(getElementUpdates(this.inputControl, 'disabled', (value) => (this.disabled = value === '' ? true : value)), getElementUpdates(this.inputControl, 'aria-disabled', (value) => (this.disabled = value === 'true' ? true : false)), getElementUpdates(this.inputControl, 'readonly', (value) => (this.readonly = value === '' ? true : value)));
    }
    setupHTML5Validation() {
        if (!this.inputControl?.form?.noValidate && this.validate) {
            syncHTML5Validation(this, Array.from(this.messages));
        }
    }
    setupPositioningListeners() {
        this.setActionOffsetPadding();
        // https://github.com/vmware-clarity/core/issues/182
        this.observers.push(elementVisible(this.inputControl, () => this.setActionOffsetPadding()));
    }
    async setActionOffsetPadding() {
        const prefix = this.prefixAction?.updateComplete;
        const suffix = this.suffixAction?.updateComplete;
        (await prefix) ? prefix : Promise.resolve(true);
        (await suffix) ? suffix : Promise.resolve(true);
        await childrenUpdateComplete(this.controlActions);
        if (!this.isGenericControl && this.supportsPrefixSuffixActions && this.hasControlActions) {
            const start = pxToRem(this.prefixAction.getBoundingClientRect().width + 6);
            const end = pxToRem(this.suffixAction.getBoundingClientRect().width + 6);
            this.inputControl.style.setProperty('padding-left', this.isRTL ? end : start, 'important');
            this.inputControl.style.setProperty('padding-right', this.isRTL ? start : end, 'important');
        }
    }
    /** @private */
    get layoutStable() {
        return (this.labelLayout !== ControlLabelLayout.default ||
            !controlIsWrapped(this.inputControl, this.controlLabel, this.layout));
    }
    setupResponsive() {
        if (this.responsive && this.labelLayout === ControlLabelLayout.default && this.controlLabel) {
            const layoutConfig = { layouts: formLayouts, initialLayout: this.layout };
            this.addEventListener('cdsResizeChange', () => {
                calculateOptimalLayout(this, layoutConfig).then(updated => {
                    if (updated) {
                        this.layoutChange.emit(this.layout, { bubbles: true });
                    }
                });
            });
        }
    }
    setupLabelLayout() {
        if (this.label?.getAttribute('cds-layout')?.includes('display:screen-reader-only')) {
            this.labelLayout = ControlLabelLayout.hiddenLabel;
        }
        if (this.hasAriaLabelTypeAttr) {
            this.labelLayout = ControlLabelLayout.ariaLabel;
        }
    }
    assignSlotIfInControlGroup() {
        if (this.parentElement?.hasAttribute('cds-control-group')) {
            this.setAttribute('slot', 'controls');
        }
    }
};
__decorate([
    property({ type: String })
], CdsControl.prototype, "status", void 0);
__decorate([
    property({ type: String })
], CdsControl.prototype, "controlWidth", void 0);
__decorate([
    property({ type: Boolean })
], CdsControl.prototype, "validate", void 0);
__decorate([
    property({ type: Boolean })
], CdsControl.prototype, "responsive", void 0);
__decorate([
    property({ type: String })
], CdsControl.prototype, "layout", null);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsControl.prototype, "focused", void 0);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsControl.prototype, "disabled", void 0);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsControl.prototype, "readonly", void 0);
__decorate([
    state()
], CdsControl.prototype, "fixedControlWidth", void 0);
__decorate([
    state()
], CdsControl.prototype, "supportsPrefixSuffixActions", void 0);
__decorate([
    state()
], CdsControl.prototype, "isRTL", null);
__decorate([
    state()
], CdsControl.prototype, "labelLayout", void 0);
__decorate([
    querySlot('input, select, textarea, [cds-control]', {
        required: 'error',
        requiredMessage: 'input element is missing',
        assign: 'input',
    })
], CdsControl.prototype, "inputControl", void 0);
__decorate([
    querySlot('label', {
        required: 'error',
        requiredMessage: 'To meet a11y standards either a <label> or input[aria-label] should be provided.',
        assign: 'label',
        exemptOn: _this => {
            return _this.hasAriaLabelTypeAttr;
        },
    })
], CdsControl.prototype, "label", void 0);
__decorate([
    query('cds-internal-control-label[action=primary]')
], CdsControl.prototype, "controlLabel", void 0);
__decorate([
    querySlot('datalist', { assign: 'datalist' })
], CdsControl.prototype, "datalistControl", void 0);
__decorate([
    querySlotAll('cds-control-message')
], CdsControl.prototype, "messages", void 0);
__decorate([
    querySlotAll('[cds-button-action]')
], CdsControl.prototype, "controlActions", void 0);
__decorate([
    query('.prefix')
], CdsControl.prototype, "prefixAction", void 0);
__decorate([
    query('.suffix')
], CdsControl.prototype, "suffixAction", void 0);
__decorate([
    query('.messages')
], CdsControl.prototype, "messageSlot", void 0);
__decorate([
    event()
], CdsControl.prototype, "layoutChange", void 0);
CdsControl = __decorate([
    responsive()
], CdsControl);
export { CdsControl };
